<ion-header>
  <div class="flex-space-between" style="padding: 15px">
    <div class="button-container flexed-align-center">
      <ion-button 
        (click)="onRefresh()" 
        [disabled]="isLoading || !canRefresh" 
        class="hidden-sm"
        fill="outline">
        <ion-icon name="refresh-outline" slot="start"></ion-icon>
        <span class="hidden-sm">Refresh</span>
      </ion-button>
      <span *ngIf="lastRefresh" class="text-muted" style="margin-left: 15px; font-size: 0.9em;">
        Last updated: {{ lastRefresh | date:'short' }}
        <span class="auto-refresh-note">(refreshes automatically)</span>
      </span>
    </div>
  </div>
</ion-header>

<ion-content>
  <!-- Loading State -->
  <div *ngIf="isLoading && !deploymentData" class="flex-center" style="height: 200px;">
    <ion-spinner></ion-spinner>
    <span style="margin-left: 10px;">Loading deployment status...</span>
  </div>

  <!-- Error Banner -->
  <ion-card *ngIf="error" color="danger" style="margin: 15px 60px; max-width: 1400px; margin-left: auto; margin-right: auto;">
    <ion-card-content>
      <div class="flex-space-between">
        <div>
          <ion-icon name="alert-circle-outline" style="margin-right: 8px;"></ion-icon>
          {{ error }}
        </div>
        <ion-button fill="clear" size="small" (click)="dismissError()">
          <ion-icon name="close-outline"></ion-icon>
        </ion-button>
      </div>
    </ion-card-content>
  </ion-card>

  <!-- API Errors Warning -->
  <ion-card *ngIf="deploymentData?.errors" color="warning" style="margin: 15px 60px; max-width: 1400px; margin-left: auto; margin-right: auto;">
    <ion-card-content>
      <div>
        <ion-icon name="warning-outline" style="margin-right: 8px;"></ion-icon>
        <strong>Some data may be incomplete:</strong>
        <ul style="margin: 10px 0 0 20px; padding: 0;">
          <li *ngIf="deploymentData.errors.github">GitHub: {{ deploymentData.errors.github }}</li>
        </ul>
      </div>
    </ion-card-content>
  </ion-card>

  <!-- Deployment Data -->
  <div *ngIf="deploymentData" class="deployment-grid" style="padding: 15px 60px; display: grid; grid-template-columns: 1fr 1fr; gap: 30px; max-width: 1400px; margin: 0 auto; align-items: start;">
    
    <!-- Production Environment -->
    <div class="environment-card" [attr.data-status]="getEnvironmentStatus(deploymentData.production).color">
      <!-- Status Header -->
      <div class="status-header" [ngClass]="'status-' + getEnvironmentStatus(deploymentData.production).color">
        <div class="environment-name">PRODUCTION</div>
        <div class="status-badge">{{ getEnvironmentStatus(deploymentData.production).urgency }}</div>
        <div *ngIf="deploymentData.production.pendingCommitsCount > 0" class="pending-count">
          {{ deploymentData.production.pendingCommitsCount }} commit{{ deploymentData.production.pendingCommitsCount !== 1 ? 's' : '' }} behind
        </div>
      </div>

      <div class="card-content">
        <!-- Latest Deploy Info -->
        <div *ngIf="deploymentData.production.latestDeploy" class="deploy-info">
          <div class="deploy-time">
            <span class="time-relative">{{ getEnvironmentStatus(deploymentData.production).timeAgo }}</span>
            <span class="time-absolute">{{ formatDeployTime(deploymentData.production.latestDeploy.deployedAt) }}</span>
          </div>
          <div class="deploy-commit">
            Latest: <a [href]="getGitHubCommitUrl(deploymentData.production.latestDeploy.commitSha)" target="_blank" class="commit-link">
              <strong>{{ deploymentData.production.latestDeploy.shortSha }}</strong>
            </a>
          </div>
        </div>

        <!-- No Deployment State -->
        <div *ngIf="!deploymentData.production.latestDeploy" class="no-deploy">
          <ion-icon name="information-circle-outline"></ion-icon>
          No deployments recorded yet
        </div>

        <!-- Pending Changes Section -->
        <div *ngIf="deploymentData.production.latestDeploy" class="changes-section">
          
          <!-- Up to Date State -->
          <div *ngIf="deploymentData.production.pendingCommitsCount === 0" class="up-to-date-section">
            <div class="section-header">
              <span>Latest Features Shipped</span>
              <span class="commit-summary" *ngIf="deploymentData.production.shippedCommitsCount > 0">{{ getCommitSummary(deploymentData.production.shippedCommits) }}</span>
            </div>
            
            <!-- Show shipped commits if available -->
            <div *ngIf="deploymentData.production.shippedCommitsCount > 0" class="commits-list">
              <div *ngFor="let commit of getVisibleCommits(deploymentData.production.shippedCommits, 'production-shipped').visible" class="commit-item">
                <div class="commit-icon">
                  <ion-icon *ngIf="commit.githubPr" name="git-pull-request" color="medium"></ion-icon>
                  <ion-icon *ngIf="!commit.githubPr" name="git-commit" color="success"></ion-icon>
                </div>
                <div class="commit-content">
                  <a *ngIf="commit.githubPr" [href]="commit.githubPr.url" target="_blank" class="commit-main-link">
                    <span class="commit-title">{{ cleanPRTitle(commit.githubPr.title) }}</span>
                    <span class="commit-meta">
                      PR #{{ commit.githubPr.number }}
                      <span class="commit-time" [title]="formatCommitTime(commit.commitDate)">
                        · {{ getCommitRelativeTime(commit.commitDate) }}
                      </span>
                    </span>
                  </a>
                  <a *ngIf="!commit.githubPr" [href]="commit.githubUrl" target="_blank" class="commit-main-link">
                    <span class="commit-title">{{ commit.message }}</span>
                    <span class="commit-meta">
                      {{ commit.shortSha }}
                      <span class="commit-time" [title]="formatCommitTime(commit.commitDate)">
                        · {{ getCommitRelativeTime(commit.commitDate) }}
                      </span>
                    </span>
                  </a>
                </div>
              </div>
              
              <div *ngIf="getVisibleCommits(deploymentData.production.shippedCommits, 'production-shipped').remaining > 0" 
                   class="remaining-commits clickable" 
                   (click)="toggleCommitsExpanded('production-shipped')">
                + {{ getVisibleCommits(deploymentData.production.shippedCommits, 'production-shipped').remaining }} more commit{{ getVisibleCommits(deploymentData.production.shippedCommits, 'production-shipped').remaining !== 1 ? 's' : '' }}
                <ion-icon name="chevron-down"></ion-icon>
              </div>
              
              <div *ngIf="expandedCommits['production-shipped'] && deploymentData.production.shippedCommits.length > 4" 
                   class="remaining-commits clickable" 
                   (click)="toggleCommitsExpanded('production-shipped')">
                Show fewer commits
                <ion-icon name="chevron-up"></ion-icon>
              </div>
            </div>
            
            <!-- Fallback message if no shipped commits available -->
            <div *ngIf="deploymentData.production.shippedCommitsCount === 0" class="up-to-date-message">
              <ion-icon name="checkmark-circle" color="success"></ion-icon>
              <span>Up to date with main branch</span>
            </div>
          </div>

          <!-- Pending Commits -->
          <div *ngIf="deploymentData.production.pendingCommitsCount > 0" class="pending-section">
            <div class="section-header">
              <span>Features Pending Deploy</span>
              <span class="commit-summary">{{ getCommitSummary(deploymentData.production.pendingCommits) }}</span>
            </div>
            
            <div class="commits-list">
              <div *ngFor="let commit of getVisibleCommits(deploymentData.production.pendingCommits, 'production').visible" class="commit-item">
                <div class="commit-icon">
                  <ion-icon *ngIf="commit.githubPr" name="git-pull-request" color="medium"></ion-icon>
                  <ion-icon *ngIf="!commit.githubPr" name="git-commit" color="medium"></ion-icon>
                </div>
                <div class="commit-content">
                  <a *ngIf="commit.githubPr" [href]="commit.githubPr.url" target="_blank" class="commit-main-link">
                    <span class="commit-title">{{ cleanPRTitle(commit.githubPr.title) }}</span>
                    <span class="commit-meta">
                      PR #{{ commit.githubPr.number }}
                      <span class="commit-time" [title]="formatCommitTime(commit.commitDate)">
                        · {{ getCommitRelativeTime(commit.commitDate) }}
                      </span>
                    </span>
                  </a>
                  <a *ngIf="!commit.githubPr" [href]="commit.githubUrl" target="_blank" class="commit-main-link">
                    <span class="commit-title">{{ commit.message }}</span>
                    <span class="commit-meta">
                      {{ commit.shortSha }}
                      <span class="commit-time" [title]="formatCommitTime(commit.commitDate)">
                        · {{ getCommitRelativeTime(commit.commitDate) }}
                      </span>
                    </span>
                  </a>
                </div>
              </div>
              
              <div *ngIf="getVisibleCommits(deploymentData.production.pendingCommits, 'production').remaining > 0" 
                   class="remaining-commits clickable" 
                   (click)="toggleCommitsExpanded('production')">
                + {{ getVisibleCommits(deploymentData.production.pendingCommits, 'production').remaining }} more commit{{ getVisibleCommits(deploymentData.production.pendingCommits, 'production').remaining !== 1 ? 's' : '' }}
                <ion-icon name="chevron-down"></ion-icon>
              </div>
              
              <div *ngIf="expandedCommits['production'] && deploymentData.production.pendingCommits.length > 4" 
                   class="remaining-commits clickable" 
                   (click)="toggleCommitsExpanded('production')">
                Show fewer commits
                <ion-icon name="chevron-up"></ion-icon>
              </div>
            </div>
          </div>
        </div>

        <!-- Previous Deploy - Only show when there's a latest deployment -->
        <div *ngIf="deploymentData.production.latestDeploy" class="previous-deploy">
          <div *ngIf="deploymentData.production.previousDeploy; else noPreviousDeploy">
            Previous: <a [href]="getGitHubCommitUrl(deploymentData.production.previousDeploy.commitSha)" target="_blank" class="commit-link">
              <strong>{{ deploymentData.production.previousDeploy.shortSha }}</strong>
            </a>
            {{ formatDeployTime(deploymentData.production.previousDeploy.deployedAt) }}
          </div>
          <ng-template #noPreviousDeploy>
            <span class="text-muted">No previous deployment recorded</span>
          </ng-template>
        </div>
      </div>
    </div>

    <!-- Staging Environment -->
    <div class="environment-card" [attr.data-status]="getEnvironmentStatus(deploymentData.staging).color">
      <!-- Status Header -->
      <div class="status-header" [ngClass]="'status-' + getEnvironmentStatus(deploymentData.staging).color">
        <div class="environment-name">STAGING (WIP)</div>
        <div class="status-badge">{{ getEnvironmentStatus(deploymentData.staging).urgency }}</div>
        <div *ngIf="deploymentData.staging.pendingCommitsCount > 0" class="pending-count">
          {{ deploymentData.staging.pendingCommitsCount }} commit{{ deploymentData.staging.pendingCommitsCount !== 1 ? 's' : '' }} behind
        </div>
      </div>

      <div class="card-content">
        <!-- Latest Deploy Info -->
        <div *ngIf="deploymentData.staging.latestDeploy" class="deploy-info">
          <div class="deploy-time">
            <span class="time-relative">{{ getEnvironmentStatus(deploymentData.staging).timeAgo }}</span>
            <span class="time-absolute">{{ formatDeployTime(deploymentData.staging.latestDeploy.deployedAt) }}</span>
          </div>
          <div class="deploy-commit">
            Latest: <a [href]="getGitHubCommitUrl(deploymentData.staging.latestDeploy.commitSha)" target="_blank" class="commit-link">
              <strong>{{ deploymentData.staging.latestDeploy.shortSha }}</strong>
            </a>
          </div>
        </div>

        <!-- No Deployment State -->
        <div *ngIf="!deploymentData.staging.latestDeploy" class="no-deploy">
          <ion-icon name="information-circle-outline"></ion-icon>
          No deployments recorded yet
        </div>

        <!-- Pending Changes Section -->
        <div *ngIf="deploymentData.staging.latestDeploy" class="changes-section">
          
          <!-- Up to Date State -->
          <div *ngIf="deploymentData.staging.pendingCommitsCount === 0" class="up-to-date-section">
            <div class="section-header">
              <span>Latest Features Shipped</span>
              <span class="commit-summary" *ngIf="deploymentData.staging.shippedCommitsCount > 0">{{ getCommitSummary(deploymentData.staging.shippedCommits) }}</span>
            </div>
            
            <!-- Show shipped commits if available -->
            <div *ngIf="deploymentData.staging.shippedCommitsCount > 0" class="commits-list">
              <div *ngFor="let commit of getVisibleCommits(deploymentData.staging.shippedCommits, 'staging-shipped').visible" class="commit-item">
                <div class="commit-icon">
                  <ion-icon *ngIf="commit.githubPr" name="git-pull-request" color="medium"></ion-icon>
                  <ion-icon *ngIf="!commit.githubPr" name="git-commit" color="success"></ion-icon>
                </div>
                <div class="commit-content">
                  <a *ngIf="commit.githubPr" [href]="commit.githubPr.url" target="_blank" class="commit-main-link">
                    <span class="commit-title">{{ cleanPRTitle(commit.githubPr.title) }}</span>
                    <span class="commit-meta">
                      PR #{{ commit.githubPr.number }}
                      <span class="commit-time" [title]="formatCommitTime(commit.commitDate)">
                        · {{ getCommitRelativeTime(commit.commitDate) }}
                      </span>
                    </span>
                  </a>
                  <a *ngIf="!commit.githubPr" [href]="commit.githubUrl" target="_blank" class="commit-main-link">
                    <span class="commit-title">{{ commit.message }}</span>
                    <span class="commit-meta">
                      {{ commit.shortSha }}
                      <span class="commit-time" [title]="formatCommitTime(commit.commitDate)">
                        · {{ getCommitRelativeTime(commit.commitDate) }}
                      </span>
                    </span>
                  </a>
                </div>
              </div>
              
              <div *ngIf="getVisibleCommits(deploymentData.staging.shippedCommits, 'staging-shipped').remaining > 0" 
                   class="remaining-commits clickable" 
                   (click)="toggleCommitsExpanded('staging-shipped')">
                + {{ getVisibleCommits(deploymentData.staging.shippedCommits, 'staging-shipped').remaining }} more commit{{ getVisibleCommits(deploymentData.staging.shippedCommits, 'staging-shipped').remaining !== 1 ? 's' : '' }}
                <ion-icon name="chevron-down"></ion-icon>
              </div>
              
              <div *ngIf="expandedCommits['staging-shipped'] && deploymentData.staging.shippedCommits.length > 4" 
                   class="remaining-commits clickable" 
                   (click)="toggleCommitsExpanded('staging-shipped')">
                Show fewer commits
                <ion-icon name="chevron-up"></ion-icon>
              </div>
            </div>
            
            <!-- Fallback message if no shipped commits available -->
            <div *ngIf="deploymentData.staging.shippedCommitsCount === 0" class="up-to-date-message">
              <ion-icon name="checkmark-circle" color="success"></ion-icon>
              <span>Up to date with main branch</span>
            </div>
          </div>

          <!-- Pending Commits -->
          <div *ngIf="deploymentData.staging.pendingCommitsCount > 0" class="pending-section">
            <div class="section-header">
              <span>Features Pending Deploy</span>
              <span class="commit-summary">{{ getCommitSummary(deploymentData.staging.pendingCommits) }}</span>
            </div>
            
            <div class="commits-list">
              <div *ngFor="let commit of getVisibleCommits(deploymentData.staging.pendingCommits, 'staging').visible" class="commit-item">
                <div class="commit-icon">
                  <ion-icon *ngIf="commit.githubPr" name="git-pull-request" color="medium"></ion-icon>
                  <ion-icon *ngIf="!commit.githubPr" name="git-commit" color="medium"></ion-icon>
                </div>
                <div class="commit-content">
                  <a *ngIf="commit.githubPr" [href]="commit.githubPr.url" target="_blank" class="commit-main-link">
                    <span class="commit-title">{{ cleanPRTitle(commit.githubPr.title) }}</span>
                    <span class="commit-meta">
                      PR #{{ commit.githubPr.number }}
                      <span class="commit-time" [title]="formatCommitTime(commit.commitDate)">
                        · {{ getCommitRelativeTime(commit.commitDate) }}
                      </span>
                    </span>
                  </a>
                  <a *ngIf="!commit.githubPr" [href]="commit.githubUrl" target="_blank" class="commit-main-link">
                    <span class="commit-title">{{ commit.message }}</span>
                    <span class="commit-meta">
                      {{ commit.shortSha }}
                      <span class="commit-time" [title]="formatCommitTime(commit.commitDate)">
                        · {{ getCommitRelativeTime(commit.commitDate) }}
                      </span>
                    </span>
                  </a>
                </div>
              </div>
              
              <div *ngIf="getVisibleCommits(deploymentData.staging.pendingCommits, 'staging').remaining > 0" 
                   class="remaining-commits clickable" 
                   (click)="toggleCommitsExpanded('staging')">
                + {{ getVisibleCommits(deploymentData.staging.pendingCommits, 'staging').remaining }} more commit{{ getVisibleCommits(deploymentData.staging.pendingCommits, 'staging').remaining !== 1 ? 's' : '' }}
                <ion-icon name="chevron-down"></ion-icon>
              </div>
              
              <div *ngIf="expandedCommits['staging'] && deploymentData.staging.pendingCommits.length > 4" 
                   class="remaining-commits clickable" 
                   (click)="toggleCommitsExpanded('staging')">
                Show fewer commits
                <ion-icon name="chevron-up"></ion-icon>
              </div>
            </div>
          </div>
        </div>

        <!-- Previous Deploy - Only show when there's a latest deployment -->
        <div *ngIf="deploymentData.staging.latestDeploy" class="previous-deploy">
          <div *ngIf="deploymentData.staging.previousDeploy; else noPreviousDeployStaging">
            Previous: <a [href]="getGitHubCommitUrl(deploymentData.staging.previousDeploy.commitSha)" target="_blank" class="commit-link">
              <strong>{{ deploymentData.staging.previousDeploy.shortSha }}</strong>
            </a>
            {{ formatDeployTime(deploymentData.staging.previousDeploy.deployedAt) }}
          </div>
          <ng-template #noPreviousDeployStaging>
            <span class="text-muted">No previous deployment recorded</span>
          </ng-template>
        </div>
      </div>
    </div>

  </div>

  <!-- Loading Overlay -->
  <div *ngIf="isLoading && deploymentData" class="loading-overlay">
    <ion-spinner></ion-spinner>
  </div>

</ion-content> 